/** FirstLight style for the web — the same template the iOS app ships
 * (src/map/template.json is a byte copy of the app's firstlight.json),
 * with the same dynamic additions FirstLightStyle.swift makes at runtime.
 * Port rules: behavior follows the Swift file COMMENT FOR COMMENT — when
 * in doubt, read ios/Dustlark/Map/FirstLightStyle.swift; the ordering
 * tricks there (the ustopo partition especially) were paid for in field
 * bugs. Sources point at pmtiles:// URLs on tiles.dustlark.app.
 *
 * buildStyle() is pure (manifest in, style JSON out) so tests can assert
 * invariants without a browser. */

import template from "./template.json";
import SYMBOLOGY_TABLE from "./tables/symbology-table.json";
import CASING_PAIRS from "./tables/casing-pairs.json";
import { layerURL, type StateManifest, TILES } from "../catalog";

export type MapLook =
  | "outdoors"
  | "dustlarkTopo"
  | "ustopo"
  | "hillshade"
  | "historic"
  | "imagery";

export const LOOK_LABELS: Record<MapLook, string> = {
  outdoors: "Outdoors",
  dustlarkTopo: "Dustlark Topo",
  ustopo: "US Topo",
  hillshade: "Hillshade",
  historic: "1950s Topo",
  imagery: "Satellite",
};

/** Canonical cycle order — fixed regardless of what a given state serves;
 * nextLook/availableLooks below both walk this same sequence. */
const LOOK_ORDER: MapLook[] = [
  "outdoors",
  "dustlarkTopo",
  "ustopo",
  "hillshade",
  "historic",
  "imagery",
];

/** Whether the state serves enough for this look to render anything.
 * "imagery" is special: it's reachable off EITHER the corridor-scale
 * imagery layer or the statewide imagerybase layer — a future state may
 * ship base-only (see applyLook's imagery branch in firstlight-looks.ts). */
function hasLook(l: MapLook, m: StateManifest): boolean {
  if (l === "hillshade") return layerURL(m, "hillshade") !== null;
  if (l === "historic") return layerURL(m, "historic") !== null;
  if (l === "imagery") {
    return layerURL(m, "imagery") !== null || layerURL(m, "imagerybase") !== null;
  }
  if (l === "ustopo") return layerURL(m, "ustopo") !== null;
  return true; // outdoors, dustlarkTopo — always available
}

/** The looks this state can actually render, in cycle order — what the
 * look control's long-press menu (LookMenu.tsx) lists, and what nextLook
 * below cycles through. */
export function availableLooks(m: StateManifest): MapLook[] {
  return LOOK_ORDER.filter((l) => hasLook(l, m));
}

/** The look cycle, gated on what the state actually serves — mirrors
 * MapLook.next(region:). Walks the fixed LOOK_ORDER (not availableLooks'
 * filtered list) so the scan starts from `current`'s own canonical
 * position even when `current` itself isn't available (e.g. right after
 * switching to a state that dropped it). */
export function nextLook(current: MapLook, m: StateManifest): MapLook {
  const avail = availableLooks(m);
  let i = LOOK_ORDER.indexOf(current);
  for (let step = 0; step < LOOK_ORDER.length; step++) {
    i = (i + 1) % LOOK_ORDER.length;
    if (avail.includes(LOOK_ORDER[i])) return LOOK_ORDER[i];
  }
  return "outdoors";
}

/** ===================== Symbology v2: toggles ==========================
 *
 * Every table below is GENERATED from src/map/tables/symbology-table.json
 * (emitted by tools/symbology/emit_table.py, byte-identical to the copy in
 * the iOS bundle) — not hand-typed here. That file is the one place the
 * map's rows, groups, presets and legend live, and ios/Dustlark reads the
 * same 43 rows to build LayerToggles.groups. A drift is a bug, and under
 * v2 the only way to drift is to edit the generator.
 *
 * The 43 row keys are spelled out as a union purely so a typo at a call
 * site is a compile error rather than a silently-undefined switch; the
 * VALUES (labels, groups, layer ids, defaults, bit positions) all come from
 * the table, and tests/symbology-table.test.ts pins the union against it. */
export type ToggleKey =
  // bit 0-9 — the ten keys that shipped in v1. THE POSITIONS ARE LOAD-
  // BEARING: the phone folds `1 << bitIndex` into its Scout cache filename
  // (FirstLightStyle.swift), so these ten append-only positions can never
  // be reordered without silently re-keying every cached drape style.
  | "trails"
  | "hiking"
  | "geology"
  | "plss"
  | "ownership"
  | "contours"
  | "pois"
  | "parcels"
  | "labels"
  | "slope"
  // bit 10-36 — the v2 rows, appended in bitIndex order.
  | "basemapRoads"
  | "trails4x4"
  | "trailsATV"
  | "trailsMoto"
  | "unverifiedTracks"
  | "closedRoutes"
  | "seasonalTicks"
  | "ohvAllowed"
  | "boundaries"
  | "wilderness"
  | "localLand"
  | "landcover"
  | "contourLabels"
  | "water"
  | "waterNames"
  | "gates"
  | "camps"
  | "fuel"
  | "trailheadParking"
  | "waterSources"
  | "restrooms"
  | "picnic"
  | "information"
  | "viewpoints"
  | "peaks"
  | "fords"
  | "bridgesTunnels"
  // No bit — runtime overlays. They have no baked layer to flip, so they
  // stay MapView/MapCanvas props (the phone keeps them outside
  // LayerToggles for the same reason). They are still rows, because a
  // preset that doesn't write them is a preset that leaves the map half
  // in the previous mode.
  | "showUTMGrid"
  | "showMud"
  | "showWiki"
  | "showPastTrips"
  | "showCoverage"
  | "hikeMode";

export type SymbologyGroupId =
  | "roads"
  | "land"
  | "terrain"
  | "water"
  | "places"
  | "hazards"
  | "reference";

export type PresetName = "driving" | "planning" | "hiking" | "minimal";

export interface SymbologyRow {
  key: ToggleKey;
  label: string;
  group: SymbologyGroupId;
  /** Style layer ids this row flips. Empty for `hikeMode`, which changes
   * how a layer is BUILT rather than whether it draws. */
  layers: string[];
  defaultOn: boolean;
  /** True when the row's layers are drawn at runtime from the camera or a
   * local store rather than baked into the generated style. */
  runtimeOverlay: boolean;
  /** Bit position in the phone's toggle mask; null for runtime overlays. */
  bitIndex: number | null;
}

export interface SymbologyGroup {
  id: SymbologyGroupId;
  label: string;
}

export interface SymbologyPreset {
  label: string;
  blurb: string;
  /** Every one of the 43 row keys — a preset never leaves a row alone. */
  on: Record<string, boolean>;
}

export type LayerToggles = Record<ToggleKey, boolean>;

const TABLE = SYMBOLOGY_TABLE as unknown as {
  groups: SymbologyGroup[];
  rows: SymbologyRow[];
  presets: Record<PresetName, SymbologyPreset>;
  presetOrder: PresetName[];
};

/** The seven groups, in the table's own order — the order the Layers panel
 * renders its sections and the phone renders its sheet. */
export const SYMBOLOGY_GROUPS: readonly SymbologyGroup[] = TABLE.groups;

/** Rows that flip baked style layers, in bit order: 0-9 are v1's ten keys
 * in their shipped positions, 10-36 the v2 additions. Sorted explicitly so
 * the guarantee survives a generator that ever emits rows in some other
 * order. */
const BAKED_ROWS: readonly SymbologyRow[] = TABLE.rows
  .filter((r) => !r.runtimeOverlay && r.bitIndex !== null)
  .slice()
  .sort((a, b) => (a.bitIndex as number) - (b.bitIndex as number));

/** Rows with no baked layer — UTM grid, mud, wiki, past trips, coverage,
 * Hike mode — in table order, after the baked rows. */
const RUNTIME_ROWS: readonly SymbologyRow[] = TABLE.rows.filter((r) => r.runtimeOverlay);

export const SYMBOLOGY_ROWS: readonly SymbologyRow[] = [...BAKED_ROWS, ...RUNTIME_ROWS];

/** All 43 keys, baked-in-bit-order then runtime overlays. */
export const TOGGLE_KEYS: readonly ToggleKey[] = SYMBOLOGY_ROWS.map((r) => r.key);

const ROW_BY_KEY = new Map<ToggleKey, SymbologyRow>(SYMBOLOGY_ROWS.map((r) => [r.key, r]));

export function symbologyRow(key: ToggleKey): SymbologyRow {
  const row = ROW_BY_KEY.get(key);
  if (!row) throw new Error(`no symbology row for toggle "${key}"`);
  return row;
}

/** The runtime-overlay rows the WEB PLANNER actually draws. The phone has
 * all six; the planner has only the UTM grid (it has no soils extra, no
 * wiki extra, no trip store and no offline downloads to shade). The other
 * five stay in `LayerToggles` regardless — presets write all 43 keys and
 * detectPreset compares all 43, so dropping them from the state would make
 * a preset picked on the laptop mean something different from the same
 * preset picked on the phone — they simply get no row in the panel. */
export const WEB_RUNTIME_OVERLAYS: ReadonlySet<ToggleKey> = new Set<ToggleKey>(["showUTMGrid"]);

/** Over imagery, applyLook splits each cased network line into a dark core
 * plus a light `<fill>-sleeve` (firstlight-looks.ts). A sleeve is not a row
 * of its own — it is the imagery half of its casing — so it rides with
 * whichever row owns that casing. Deriving it here rather than listing it
 * in the table keeps the generator honest about what a ROW is, and keeps
 * "hiking off" from stranding a bright sleeve on a photograph with no line
 * inside it (style.test.ts pins exactly that). */
function withSleeves(ids: readonly string[]): string[] {
  const pairs = CASING_PAIRS as Record<string, string>;
  const out = [...ids];
  for (const id of ids) {
    const fill = pairs[id];
    if (fill) out.push(`${fill}-sleeve`);
  }
  return out;
}

export const DEFAULT_TOGGLES: LayerToggles = Object.fromEntries(
  SYMBOLOGY_ROWS.map((r) => [r.key, r.defaultOn]),
) as LayerToggles;

/** toggle key -> style layer ids, kept in lockstep with
 * LayerToggles.groups in ios/Dustlark/Map/MapView.swift — both sides now
 * BUILD this from symbology-table.json rather than hand-typing it, which
 * is what finally makes "a drift is a bug" enforceable rather than merely
 * asserted.
 *
 * Baked rows only: a runtime overlay has no layer in the generated style
 * for applyToggles to flip (its ids are created by map/utm-grid.ts and
 * friends after the style loads), so it is deliberately absent here and
 * reaches the map as its own MapCanvas prop instead. Iteration order is
 * bit order, 0-36. */
export const TOGGLE_GROUPS: Record<string, string[]> = Object.fromEntries(
  BAKED_ROWS.map((r) => [r.key, withSleeves(r.layers)]),
);

export const PRESET_ORDER: readonly PresetName[] = TABLE.presetOrder;
export const PRESETS: Record<PresetName, SymbologyPreset> = TABLE.presets;

/** A preset writes EVERY row, including the runtime overlays — that is the
 * whole difference between this and v1's Map-mode tiles, which set eight
 * switches and left seven alone, so a rider who turned PLSS on and then
 * tapped "Driving" kept the section grid with no way to tell why.
 *
 * The `?? DEFAULT_TOGGLES` arm can't fire against a well-formed table (the
 * generator emits all 43 keys in every preset and symbology-table.test.ts
 * pins that); it is here so a half-written preset degrades to the row's own
 * default instead of `undefined`, which React would read as "off" on some
 * rows and leave uncontrolled on others. */
export function applyPreset(name: PresetName): LayerToggles {
  const on = PRESETS[name].on;
  return Object.fromEntries(
    TOGGLE_KEYS.map((k) => [k, on[k] ?? DEFAULT_TOGGLES[k]]),
  ) as LayerToggles;
}

/** Which preset chip to light up — "custom" the moment any one of the 43
 * rows disagrees with all four. Checked in presetOrder so a state that two
 * presets somehow agree on reports the first one listed. */
export function detectPreset(state: LayerToggles): PresetName | "custom" {
  for (const name of PRESET_ORDER) {
    const preset = applyPreset(name);
    if (TOGGLE_KEYS.every((k) => state[k] === preset[k])) return name;
  }
  return "custom";
}

/** Why a row can't be switched right now, or null when it can.
 *
 * Asked of the STYLE rather than of a `requires` string, which is what lets
 * one check answer both gates. v1 only knew the first: it captioned a row
 * "Not available in this state yet" when the manifest lacked the layer, and
 * had nothing at all to say about a LOOK that strips one — so over
 * Satellite and 1950s Topo, which remove every `own-*` layer outright, the
 * ownership switch sat there fully on, doing nothing, above a four-colour
 * key describing fills the map was not drawing.
 *
 * `liveIds` is the style about to be shown; `baselineIds` is the same state
 * on Outdoors. Present in neither = this state never carried it; present in
 * the baseline only = this look takes it away. */
export function rowGate(
  row: SymbologyRow,
  liveIds: ReadonlySet<string>,
  baselineIds: ReadonlySet<string>,
): "state" | "look" | null {
  // Runtime overlays are camera math and local stores — no manifest layer
  // to miss and no look block to strip them.
  if (row.runtimeOverlay) return null;
  if (row.layers.some((id) => liveIds.has(id))) return null;
  return row.layers.some((id) => baselineIds.has(id)) ? "look" : "state";
}

export type GroupState = "on" | "off" | "mixed";

/** Tri-state for a group's master switch, over exactly the keys the caller
 * is showing — a group whose unavailable rows were filtered out must read
 * "on" when every row the rider can actually see is on, or the master
 * switch is lying about the panel in front of him. */
export function groupStateFor(keys: readonly ToggleKey[], toggles: LayerToggles): GroupState {
  if (keys.length === 0) return "off";
  const on = keys.filter((k) => toggles[k]).length;
  if (on === 0) return "off";
  return on === keys.length ? "on" : "mixed";
}

/** Master-switch click: mixed and off both go fully on, on goes fully off. */
export function setGroup(
  keys: readonly ToggleKey[],
  toggles: LayerToggles,
  value: boolean,
): LayerToggles {
  const next = { ...toggles };
  for (const k of keys) next[k] = value;
  return next;
}

export interface BuildOptions {
  look: MapLook;
  night: boolean;
  /** Sun position for the outdoors live hillshade; null skips the layer. */
  sun?: { azimuth: number; altitude: number } | null;
  /** How heavily the map's own markings are drawn — label sizes, halos,
   * line widths, and (on high contrast) the network's colours. Mirrors
   * styleURL's `markings:` parameter on the phone. Omitted means
   * `standard`, which is a no-op: the style comes out byte-identical to
   * what this builder produced before the setting existed. */
  markings?: MapMarkings;
}

type AnyLayer = Record<string, any>;
type StyleJSON = {
  version: number;
  glyphs: string;
  sources: Record<string, any>;
  layers: AnyLayer[];
  [k: string]: any;
};

/** THE style builder. Ported blocks are implemented in firstlight-looks.ts
 * (looks, night, geology) to keep each file reviewable; this assembles. */
import {
  applyNight,
  applyLook,
  applyMarkings,
  injectBLM,
  injectHiking,
  injectGeology,
  injectPLSS,
  injectLiveSun,
} from "./firstlight-looks";
import { DEFAULT_MARKINGS, type MapMarkings } from "./mapMarkings";

export function buildStyle(m: StateManifest, opts: BuildOptions): StyleJSON {
  const style = structuredClone(template) as unknown as StyleJSON;

  // ---- template source substitutions (Swift pm(_:)) --------------------
  const pm = (name: string) => {
    const u = layerURL(m, name);
    return u ? `pmtiles://${u}` : "";
  };
  const subs: Record<string, string> = {
    __BASEMAP__: pm("basemap"),
    __TRAILS__: pm("trails"),
    __OWNERSHIP__: pm("ownership"),
    __CONTOURS__: pm("contours"),
    __POIS__: pm("pois"),
    __PARCELS__: pm("parcels"),
    __SLOPE__: pm("slope"),
  };
  for (const src of Object.values(style.sources)) {
    if (typeof src.url === "string" && subs[src.url] !== undefined) {
      src.url = subs[src.url];
    }
  }
  style.glyphs = `${TILES}/glyphs/{fontstack}/{range}.pbf`;

  // ---- dynamic layers, same order as Swift -----------------------------
  // injectBLM gets the RAW night flag (not gated on paperLook) — matches
  // Swift, where the BLM block runs before the paper-look/nightify check
  // and branches on `night` directly since its blue isn't in NIGHT_MAP.
  injectBLM(style, m, opts.night);
  injectHiking(style, m, opts.look);

  // Night before look mutations; paper/photo looks stay daylight.
  const paperLook =
    opts.look === "historic" ||
    opts.look === "imagery" ||
    opts.look === "dustlarkTopo" ||
    opts.look === "ustopo";
  if (opts.night && !paperLook) applyNight(style);

  applyLook(style, m, opts.look, opts.night);
  injectGeology(style, m, opts.night && !paperLook, opts.night);
  injectPLSS(style, m, opts.night && !paperLook);
  if (opts.look === "outdoors" && opts.sun) {
    injectLiveSun(style, m, opts.sun, opts.night);
  }

  // Map markings LAST — after every look block, geology and PLSS, and
  // before the scrub below (the web stand-in for Swift's toggle bake /
  // symbol drop). It multiplies what the blocks above finally decided, so
  // it has to see their output, not race it; see applyMarkings' own
  // comment in firstlight-looks.ts.
  applyMarkings(style, opts.markings ?? DEFAULT_MARKINGS, opts.look, opts.night);

  // MapLibre GL JS (unlike MLNMapView) fails hard on a source with an
  // empty URL — scrub sources for layers this state doesn't carry, and
  // every style layer that references them (the Swift dropSymbols block's
  // scrub, applied unconditionally on web).
  const dead = Object.entries(style.sources)
    .filter(([, s]) => typeof s.url === "string" && s.url === "")
    .map(([id]) => id);
  for (const id of dead) delete style.sources[id];
  style.layers = style.layers.filter(
    (l) => !dead.includes(l.source as string),
  );

  return style;
}

/** Live visibility flip — the web analog of applyLayerVisibility. */
export function applyToggles(
  map: { getLayer(id: string): unknown; setLayoutProperty(id: string, k: string, v: string): void },
  toggles: LayerToggles,
) {
  for (const [key, ids] of Object.entries(TOGGLE_GROUPS)) {
    const visible = toggles[key as ToggleKey];
    for (const id of ids) {
      if (map.getLayer(id)) {
        map.setLayoutProperty(id, "visibility", visible ? "visible" : "none");
      }
    }
  }
}
